/**
 * SecurityPanel — last logins / SSH keys / fail2ban / sudo history.
 *
 * Backend: /api/v1/devops/security/{last,ssh_keys,fail2ban,sudo}.
 */

import { useEffect, useState } from "react";
import { Key, Shield, History, RefreshCw, Loader2, Users } from "lucide-react";
import {
  fetchLastLogins,
  fetchSshKeys,
  fetchFail2ban,
  fetchSudoHistory,
  type SshKeyItem,
} from "../../lib/api";
import { cn } from "../../lib/cn";

type Section = "logins" | "keys" | "fail2ban" | "sudo";

export function SecurityPanel() {
  const [section, setSection] = useState<Section>("logins");

  return (
    <div className="space-y-3">
      <div className="flex gap-1 flex-wrap">
        <Btn active={section === "logins"} onClick={() => setSection("logins")} icon={<Users className="size-3.5" />} label="Last logins" />
        <Btn active={section === "keys"} onClick={() => setSection("keys")} icon={<Key className="size-3.5" />} label="SSH keys" />
        <Btn active={section === "fail2ban"} onClick={() => setSection("fail2ban")} icon={<Shield className="size-3.5" />} label="fail2ban" />
        <Btn active={section === "sudo"} onClick={() => setSection("sudo")} icon={<History className="size-3.5" />} label="sudo history" />
      </div>
      {section === "logins" && <LoginsSection />}
      {section === "keys" && <SshKeysSection />}
      {section === "fail2ban" && <Fail2banSection />}
      {section === "sudo" && <SudoSection />}
    </div>
  );
}

function Btn({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 rounded-md text-small inline-flex items-center gap-1.5 border",
        active ? "bg-accent-500/10 text-accent-500 border-accent-500/40" : "border-border/60 text-fg-muted hover:bg-bg-subtle",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function LoginsSection() {
  const [n, setN] = useState(30);
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      setLoading(true);
      const r = await fetchLastLogins(n);
      setOutput(r.output);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [n]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <select value={n} onChange={(e) => setN(Number(e.target.value))} className="px-2 py-1.5 rounded border border-border/60 bg-bg-card/70 text-small">
          <option value={20}>20</option>
          <option value={30}>30</option>
          <option value={100}>100</option>
          <option value={200}>200</option>
        </select>
        <button type="button" onClick={load} className="px-2 py-1.5 rounded border border-border/60 hover:bg-bg-subtle text-small">
          <RefreshCw className="size-3.5" />
        </button>
      </div>
      {error && <div className="rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}
      <pre className="rounded-modal border border-border/60 bg-bg-card/40 p-3 text-[11px] font-mono whitespace-pre-wrap text-fg max-h-[60vh] overflow-auto">
        {loading ? <Loader2 className="size-4 animate-spin inline" /> : output || "(пусто)"}
      </pre>
    </div>
  );
}

function SshKeysSection() {
  const [keys, setKeys] = useState<SshKeyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const r = await fetchSshKeys();
      setKeys(r.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-2">
      <button type="button" onClick={load} className="px-2 py-1.5 rounded border border-border/60 hover:bg-bg-subtle text-small">
        <RefreshCw className="size-3.5 inline mr-1" /> Обновить
      </button>
      {error && <div className="rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}
      <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
        <table className="w-full text-small font-mono">
          <thead>
            <tr className="text-left text-fg-subtle text-[11px] border-b border-border/30 bg-bg-subtle/40">
              <th className="px-3 py-2">USER</th>
              <th className="px-3 py-2">ALGO</th>
              <th className="px-3 py-2">FINGERPRINT</th>
              <th className="px-3 py-2">COMMENT</th>
              <th className="px-3 py-2">PATH</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-4 text-center text-fg-muted"><Loader2 className="size-4 animate-spin inline" /></td></tr>}
            {!loading && keys.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-fg-muted">SSH ключей не найдено</td></tr>}
            {keys.map((k, i) => (
              <tr key={i} className="border-b border-border/20 hover:bg-bg-subtle/30">
                <td className="px-3 py-1.5 text-fg">{k.user}</td>
                <td className="px-3 py-1.5 text-fg-muted">{k.algo}</td>
                <td className="px-3 py-1.5 text-fg-muted truncate max-w-[200px]" title={k.fingerprint}>{k.fingerprint}</td>
                <td className="px-3 py-1.5 text-fg-muted truncate max-w-[260px]" title={k.comment}>{k.comment}</td>
                <td className="px-3 py-1.5 text-fg-subtle truncate max-w-[260px]">{k.path}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Fail2banSection() {
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jail, setJail] = useState("");

  async function load() {
    try {
      setLoading(true);
      const r = await fetchFail2ban(jail);
      setOutput(r.success ? r.output : `[не запущено]\n${r.stderr}\n${r.output}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={jail}
          onChange={(e) => setJail(e.target.value)}
          placeholder="jail (напр. sshd) или пусто для общего status"
          className="flex-1 max-w-sm px-2 py-1 rounded border border-border/60 bg-bg-card text-small font-mono"
        />
        <button type="button" onClick={load} className="px-2 py-1.5 rounded border border-border/60 hover:bg-bg-subtle text-small">
          <RefreshCw className="size-3.5" />
        </button>
      </div>
      {error && <div className="rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}
      <pre className="rounded-modal border border-border/60 bg-bg-card/40 p-3 text-[11px] font-mono whitespace-pre-wrap text-fg max-h-[60vh] overflow-auto">
        {loading ? <Loader2 className="size-4 animate-spin inline" /> : output || "(пусто)"}
      </pre>
    </div>
  );
}

function SudoSection() {
  const [n, setN] = useState(50);
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      setLoading(true);
      const r = await fetchSudoHistory(n);
      setOutput(r.output);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [n]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <select value={n} onChange={(e) => setN(Number(e.target.value))} className="px-2 py-1.5 rounded border border-border/60 bg-bg-card/70 text-small">
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={200}>200</option>
          <option value={1000}>1000</option>
        </select>
        <button type="button" onClick={load} className="px-2 py-1.5 rounded border border-border/60 hover:bg-bg-subtle text-small">
          <RefreshCw className="size-3.5" />
        </button>
      </div>
      {error && <div className="rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}
      <pre className="rounded-modal border border-border/60 bg-bg-card/40 p-3 text-[11px] font-mono whitespace-pre-wrap text-fg max-h-[60vh] overflow-auto">
        {loading ? <Loader2 className="size-4 animate-spin inline" /> : output || "(пусто)"}
      </pre>
    </div>
  );
}
